import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "./PropertyCard.css";
import axios from "axios";

export default function PropertyCard({ data, view = "grid" }) {
  const navigate = useNavigate();
  const [imageErrors, setImageErrors] = useState({});
  const [parsedImages, setParsedImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(true);

  const goToDetails = () => {
    navigate(`/property/${data.id}`);
  };

  useEffect(() => {
    const parseImageData = (imageData) => {
      try {
        console.log("🖼️ Raw image data:", imageData);

        if (typeof imageData === "string") {
          const parsed = JSON.parse(imageData);
          console.log("📝 Parsed string image data:", parsed);
          return parsed;
        } else if (Array.isArray(imageData)) {
          console.log("📋 Array image data:", imageData);
          return imageData;
        }
        console.warn("⚠️ Unknown image data format:", imageData);
        return [];
      } catch (error) {
        console.error("❌ Error parsing image data:", error);
        return [];
      }
    };

    if (data && data.images) {
      const images = parseImageData(data.images);
      setParsedImages(images);
      setLoadingImages(false);
      console.log("✅ Final parsed images:", images);
    } else {
      setLoadingImages(false);
    }
  }, [data]);

  // Get image URL - UPDATED to remove BASE_URL
  const getImageUrl = (img) => {
    if (!img) return "";

    const imgPath = img.FilePath || img.filePath || img.url || img;
    if (!imgPath) return "";

    // If it's already a full URL, use it directly
    if (imgPath.startsWith("http")) {
      return imgPath;
    }

    // Construct URL - ✅ CHANGED: Remove BASE_URL prefix
    if (imgPath.startsWith("/")) {
      return imgPath; // ✅ CHANGED: No BASE_URL prepending
    } else {
      return `/${imgPath}`; // ✅ CHANGED: No BASE_URL prepending
    }
  };

  // Handle image loading errors
  const handleImageError = (imageUrl, imageIndex) => {
    console.error(`❌ Image failed to load:`, {
      imageUrl,
      imageIndex,
    });

    setImageErrors((prev) => ({
      ...prev,
      [imageIndex]: true,
    }));
  };

  // Handle successful image load
  const handleImageLoad = (imageUrl, imageIndex) => {
    console.log(`✅ Image loaded successfully: ${imageUrl}`);
    setImageErrors((prev) => ({
      ...prev,
      [imageIndex]: false,
    }));
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    autoplay: true,
    autoplaySpeed: 3000,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: parsedImages.length > 1,
    pauseOnHover: true,
    adaptiveHeight: true,
    focusOnSelect: false,
  };

  console.log("🔍 Image Status:", {
    totalImages: parsedImages.length,
    loadedImages: parsedImages.length - Object.keys(imageErrors).length,
    failedImages: Object.keys(imageErrors).length,
  });

  if (!data) {
    return (
      <article className={`card ${view}`}>
        <div className="card-media">
          <div className="image-placeholder">
            <span>Loading property...</span>
          </div>
        </div>
      </article>
    );
  }

  const hasValidImages = parsedImages.length > 0;

  return (
    <article className={`card ${view}`}>
      <div className="card-media">
        <div className="property-image">
          {loadingImages ? (
            <div className="image-placeholder">
              <span>Loading images...</span>
            </div>
          ) : hasValidImages ? (
            <div className="slider-container">
              <Slider {...sliderSettings}>
                {parsedImages.map((img, index) => {
                  const imageUrl = getImageUrl(img);
                  const hasError = imageErrors[index];

                  return (
                    <div key={index} className="slide-wrapper">
                      {hasError ? (
                        <div className="image-error">
                          <div className="error-content">
                            <span className="error-icon">📷</span>
                            <span>Image not available</span>
                            <small>Check image path</small>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={imageUrl}
                          alt={`${data?.name || "Property"} - Image ${
                            index + 1
                          }`}
                          className="property-main-image"
                          onError={() => handleImageError(imageUrl, index)}
                          onLoad={() => handleImageLoad(imageUrl, index)}
                          loading="lazy"
                          crossOrigin="anonymous"
                        />
                      )}
                    </div>
                  );
                })}
              </Slider>
            </div>
          ) : (
            <div className="image-placeholder">
              <div className="placeholder-content">
                <span className="placeholder-icon">🏠</span>
                <span>No Images Available</span>
              </div>
            </div>
          )}

          <div className="property-type-badge">{data.propertyType}</div>
        </div>

        <div className="card-badges">
          {data.isNew && <span className="badge new">New</span>}
          {data.featured && <span className="badge featured">Featured</span>}
        </div>
      </div>

      <div className="card-body">
        <h4 className="card-title">{data.name || data.title || "Property"}</h4>
        <p className="card-loc">
          {data.location || data.address || "Location unavailable"}
        </p>

        <div className="card-meta">
          <span>{data.beds || "-"} Beds</span>
          <span>{data.baths || "-"} Baths</span>
          <span>{data.size || data.area || "-"} sqft</span>
        </div>

        <div className="card-footer">
          <div className="price">
            ₹ {data.price?.toLocaleString() || "Contact"}
          </div>
          <button className="btn-search" onClick={goToDetails}>
            View
          </button>
        </div>
      </div>
    </article>
  );
}
